import React from 'react';
import { ArticleSourcesList } from '../../../components/ArticleSourcesList/ArticleSourcesList';
import { getParagraph } from '../../../components/componentHelpers';
import { useLayoutContext } from '../../../providers/LayoutProvider';
import { useInlineImages } from '../../../shared/useInlineImages';
import { NewsroomArticleAsideSocialMediaLinks } from '../NewsroomArticleAside/NewsroomArticleAsideSocialMediaLinks';
import {
    IParagraphHeading,
    TableOfContents,
} from '../../../components/TableOfContents/TableOfContents';
import './NewsroomArticleContent.scss';

export const NewsroomArticleContent = ({ node }) => {
    const { isPolicy } = useLayoutContext();
    const fixedBody = useInlineImages(node.body?.value);
    const hasListicle = node?.field_listicle;

    const pageCompSelector = isPolicy
        ? `field_page_components`
        : `field_components`;

    // Aggregates all paragraph headers on the page for use in Table of Contents
    const paragraphHeaders: IParagraphHeading[] = hasListicle
        ? node.relationships?.[pageCompSelector]?.filter(
              (component) => component?.type === `paragraph__paragraph_header`
          )
        : [];

    // Checks if the article is using the V2 article body component
    const isArticleV2 = node?.relationships?.[pageCompSelector]?.some(
        (comp) => comp?.type === `paragraph__article_body`
    );

    // Filters out the key takeaways and article source list paragraphs
    // because these are rendered in specific positions on the page
    const paragraphs = node.relationships?.[pageCompSelector]
        ?.filter(
            (paragraph) =>
                paragraph.type !== `paragraph__sources_list` &&
                paragraph.type !== `paragraph__key_takeaways`
        )
        .map((paragraph) => getParagraph(paragraph, isPolicy));

    const articleSources =
        node?.relationships?.[pageCompSelector]?.filter(
            (component) => component?.type === `paragraph__sources_list`
        ) ?? [];

    const keyTakeaways = node?.relationships?.[pageCompSelector]
        ?.filter((component) => component?.type === `paragraph__key_takeaways`)
        ?.map((paragraph) => getParagraph(paragraph, isPolicy));

    const highlightCards = paragraphs?.filter(
        (p) => p.type?.name === `LeaderHighlight`
    );

    return (
        <article className="newsroom-content">
            <header>
                <p className="eyebrow">
                    {!!node.relationships?.field_news_category && (
                        <span>
                            {node.relationships.field_news_category?.name}
                        </span>
                    )}
                    {!isPolicy && (
                        <span className="date">
                            {new Date(node.field_news_date).toLocaleDateString(
                                `en-us`,
                                {
                                    year: `numeric`,
                                    month: `long`,
                                    day: `numeric`,
                                }
                            )}
                        </span>
                    )}
                </p>
                <h1>{node.title}</h1>
                <p className="subtitle">{node.field_subtitle}</p>
                {isPolicy && (
                    <p className="subtitle date">{node.field_news_date}</p>
                )}
            </header>
            {isPolicy && <h3 className="typestack-eyebrow">Share Article:</h3>}
            {isPolicy && (
                <NewsroomArticleAsideSocialMediaLinks
                    node={node}
                    isPolicy={true}
                />
            )}
            {!isPolicy && (
                <h3 className="typestack-eyebrow m-only">Share Article:</h3>
            )}
            {!isPolicy && (
                <NewsroomArticleAsideSocialMediaLinks
                    className="m-only"
                    node={node}
                    isPolicy={false}
                />
            )}
            {!!highlightCards?.length && !isArticleV2 && (
                <ul className="leader-list">{highlightCards}</ul>
            )}
            {isArticleV2 ? (
                <>
                    {keyTakeaways}
                    {hasListicle && (
                        <TableOfContents headers={paragraphHeaders} />
                    )}
                    {paragraphs}
                </>
            ) : (
                <div
                    dangerouslySetInnerHTML={{
                        __html: fixedBody,
                    }}
                />
            )}
            {articleSources.map((articleSource, idx) => (
                <ArticleSourcesList
                    key={articleSource.field_header + idx}
                    sources={articleSource}
                />
            ))}
        </article>
    );
};
